import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useMaterials } from '../contexts/MaterialContext';
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';

const MaterialDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { materials, incrementViewCount, incrementDownloadCount } = useMaterials();

  const material = materials.find(m => m.id === Number(id));

  useEffect(() => {
    window.scrollTo(0, 0);
    if (id) {
      incrementViewCount(Number(id));
    }
  }, [id]);

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleDownload = async () => {
    if (material?.file_url) {
      await incrementDownloadCount(material.id);
      window.open(material.file_url, '_blank');
    }
  };

  if (!material) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 pt-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 py-12">
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <p className="text-gray-500">교육 자료를 찾을 수 없습니다.</p>
              <button
                onClick={() => navigate('/notice/materials')}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                목록으로
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 pt-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Header */}
            <div className="border-b border-gray-200 px-8 py-6">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">{material.title}</h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span>담당부서: {material.department}</span>
                <span>작성자: {material.author}</span>
                <span>연락처: {material.phone}</span>
                <span>등록일: {new Date(material.created_at).toLocaleDateString('ko-KR')}</span>
                <span>조회수: {material.view_count}</span>
              </div>
            </div>

            {/* 첨부파일 */}
            {material.file_url && (
              <div className="border-b border-gray-200 px-8 py-4 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <DocumentArrowDownIcon className="h-5 w-5 text-blue-500" />
                    <span className="text-sm font-medium text-gray-700">첨부파일:</span>
                    <span className="text-sm text-gray-600">{material.file_name}</span>
                    <span className="text-xs text-gray-400">({formatFileSize(material.file_size)})</span>
                  </div>
                  <button
                    onClick={handleDownload}
                    className="px-4 py-2 bg-[#48c9a6] text-white text-sm rounded-lg hover:bg-[#3db899] flex items-center gap-1"
                  >
                    <DocumentArrowDownIcon className="h-4 w-4" />
                    다운로드
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-400">다운로드 수: {material.download_count}</p>
              </div>
            )}

            {/* Content */}
            <div className="px-8 py-8">
              <div
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: material.content }}
              />
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-8 py-6 flex justify-end">
              <button
                onClick={() => navigate('/notice/materials')}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                목록으로
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default MaterialDetail;
